import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus,
  Landmark,
  Building2,
  Lock,
  X,
  Loader2,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import { bankAccountsApi } from "../../services/api";
import SearchableSelect, { SelectOption } from "../ui/SearchableSelect";
import PremiumModal from "../ui/PremiumModal";

interface BankAccount {
  _id: string;
  kind: "trust" | "operating";
  name: string;
  institution: string | null;
  accountLast4: string | null;
  currency: string;
  status: "open" | "closed";
  openedAt: string;
  closedAt: string | null;
}

const kindBadge: Record<string, { bg: string; text: string; Icon: React.FC<any> }> = {
  trust: { bg: "bg-blue-100", text: "text-blue-700", Icon: Landmark },
  operating: { bg: "bg-purple-100", text: "text-purple-700", Icon: Building2 },
};

const statusBadge: Record<string, { bg: string; text: string; Icon: React.FC<any> }> = {
  open: { bg: "bg-emerald-100", text: "text-emerald-700", Icon: CheckCircle2 },
  closed: { bg: "bg-red-100", text: "text-red-700", Icon: Lock },
};

function formatDate(iso: string | null): string {
  if (!iso) return "-";
  const d = new Date(iso);
  return d.toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" });
}

const BankAccountsPage: React.FC = () => {
  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState<BankAccount | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Form
  const [form, setForm] = useState({
    kind: "trust" as string,
    name: "",
    institution: "",
    accountLast4: "",
    currency: "CAD",
  });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const fetchAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await bankAccountsApi.list({ limit: 100 });
      setAccounts(res.data?.data || []);
    } catch (err) {
      console.error("Failed to load bank accounts:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  const openCreate = () => {
    setEditingAccount(null);
    setForm({ kind: "trust", name: "", institution: "", accountLast4: "", currency: "CAD" });
    setSaveError("");
    setShowCreateModal(true);
  };

  const openEdit = (account: BankAccount) => {
    setEditingAccount(account);
    setForm({
      kind: account.kind,
      name: account.name,
      institution: account.institution || "",
      accountLast4: account.accountLast4 || "",
      currency: account.currency,
    });
    setSaveError("");
    setShowCreateModal(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setSaveError("");
    try {
      if (editingAccount) {
        await bankAccountsApi.update(editingAccount._id, {
          name: form.name,
          institution: form.institution || null,
          accountLast4: form.accountLast4 || null,
          currency: form.currency,
        });
      } else {
        await bankAccountsApi.create(form);
      }
      setShowCreateModal(false);
      fetchAccounts();
    } catch (err: any) {
      setSaveError(err.response?.data?.error || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleClose = async (id: string) => {
    if (!window.confirm("Are you sure you want to close this bank account? This action cannot be undone.")) return;
    setActionLoading(id);
    try {
      await bankAccountsApi.close(id);
      fetchAccounts();
    } catch (err: any) {
      setErrorMessage(err.response?.data?.error || "Failed to close account");
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#F4F6F8" }}>
      <div className="max-w-[1200px] mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Bank Accounts</h1>
            <p className="text-sm text-slate-500 mt-1">Manage trust and operating bank accounts</p>
          </div>
          <button
            onClick={openCreate}
            className="inline-flex items-center gap-2 px-5 py-3 text-white font-bold rounded-xl transition-colors"
            style={{ backgroundColor: "var(--color-primary)" }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary-hover)")}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary)")}
          >
            <Plus className="h-5 w-5" />
            Add Account
          </button>
        </div>

        {/* Error Banner */}
        {errorMessage && (
          <div className="mb-4 flex items-center gap-2.5 rounded-xl border border-red-200 bg-red-50 p-4 text-sm font-medium text-red-700">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span className="flex-1">{errorMessage}</span>
            <button onClick={() => setErrorMessage(null)} className="ml-auto text-red-400 hover:text-red-600 transition-colors">&times;</button>
          </div>
        )}

        {/* Accounts Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
          </div>
        ) : accounts.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center">
            <Landmark className="h-12 w-12 mx-auto mb-3 text-slate-300" />
            <p className="text-lg font-medium text-slate-600">No bank accounts</p>
            <p className="text-sm text-slate-400 mt-1">Add your first bank account to get started</p>
            <button
              onClick={openCreate}
              className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 text-white font-semibold rounded-xl text-sm"
              style={{ backgroundColor: "var(--color-primary)" }}
            >
              <Plus className="h-4 w-4" />
              Add First Account
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {accounts.map((account, i) => {
              const kind = kindBadge[account.kind] || kindBadge.trust;
              const status = statusBadge[account.status] || statusBadge.open;
              const KindIcon = kind.Icon;
              const StatusIcon = status.Icon;

              return (
                <motion.div
                  key={account._id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className="bg-white rounded-lg shadow-sm border border-slate-200 p-5 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`p-2.5 rounded-lg ${kind.bg}`}>
                        <KindIcon className={`h-5 w-5 ${kind.text}`} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-800">{account.name}</h3>
                        {account.institution && (
                          <p className="text-xs text-slate-500">{account.institution}</p>
                        )}
                      </div>
                    </div>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${status.bg} ${status.text}`}>
                      <StatusIcon className="h-3 w-3" />
                      {account.status === "open" ? "Open" : "Closed"}
                    </span>
                  </div>

                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Type</span>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${kind.bg} ${kind.text}`}>
                        {account.kind === "trust" ? "Trust" : "Operating"}
                      </span>
                    </div>
                    {account.accountLast4 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-500">Account</span>
                        <span className="text-slate-700 font-mono">...{account.accountLast4}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Currency</span>
                      <span className="text-slate-700">{account.currency}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Opened</span>
                      <span className="text-slate-700">{formatDate(account.openedAt)}</span>
                    </div>
                  </div>

                  <div className="flex gap-2 pt-3 border-t border-slate-100">
                    <button
                      onClick={() => openEdit(account)}
                      className="flex-1 text-xs font-medium py-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors"
                    >
                      Edit
                    </button>
                    {account.status === "open" && (
                      <button
                        onClick={() => handleClose(account._id)}
                        disabled={actionLoading === account._id}
                        className="flex-1 text-xs font-medium py-2 rounded-lg bg-red-50 text-red-700 hover:bg-red-100 transition-colors disabled:opacity-50"
                      >
                        {actionLoading === account._id ? "..." : "Close"}
                      </button>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Create/Edit Modal */}
        <PremiumModal
          open={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          title={editingAccount ? "Edit Bank Account" : "New Bank Account"}
          icon={Landmark}
          size="md"
          footer={
            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => setShowCreateModal(false)} className="px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50">Cancel</button>
              <button
                type="submit" form="bankAccountForm" disabled={saving || !form.name}
                className="px-5 py-2.5 rounded-xl text-white text-sm font-bold disabled:opacity-50"
                style={{ backgroundColor: "var(--color-primary)" }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary-hover)")}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary)")}
              >
                {saving ? "Saving..." : editingAccount ? "Save Changes" : "Create Account"}
              </button>
            </div>
          }
        >
          {saveError && <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm">{saveError}</div>}

          <form id="bankAccountForm" onSubmit={handleSave} className="space-y-4">
            {!editingAccount && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Account Type *</label>
                <SearchableSelect
                  options={[
                    { value: "trust", label: "Trust Account" },
                    { value: "operating", label: "Operating Account" },
                  ]}
                  value={form.kind}
                  onChange={(val) => setForm({ ...form, kind: val || "trust" })}
                  placeholder="Select account type"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Account Name *</label>
              <input type="text" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600" placeholder="e.g. Main Trust Account" />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Institution</label>
              <input type="text" value={form.institution} onChange={(e) => setForm({ ...form, institution: e.target.value })} className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600" placeholder="e.g. TD Canada Trust" />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Account Last 4 Digits</label>
              <input type="text" maxLength={4} value={form.accountLast4} onChange={(e) => setForm({ ...form, accountLast4: e.target.value })} className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600" placeholder="1234" />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Currency</label>
              <SearchableSelect
                options={[
                  { value: "CAD", label: "CAD" },
                  { value: "USD", label: "USD" },
                ]}
                value={form.currency}
                onChange={(val) => setForm({ ...form, currency: val || "CAD" })}
                placeholder="Select currency"
              />
            </div>
          </form>
        </PremiumModal>
      </div>
    </div>
  );
};

export default BankAccountsPage;
